/**
 * Follow-up drawer — what a save SENDS and what a close RETURNS (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * The new drawer must keep today's contract exactly — the backend files a follow-up by the shape
 * of `tasks/addUpdateFollowupTask`'s payload, and every caller patches its row from the
 * `closePanel` result. So every builder below is copied from CreateFollowup.vue (FROZEN), key for
 * key and in the same order, quirks included:
 *   save()                       :1957  → buildSavePayload / saveBlockReason / buildCloseResult
 *   closeSlideOutPanel()         :1948  → closeWithoutSave
 *   _editFollowupNote()          :2756  → buildEditNotePayload
 *   updateFollowupDateTo()       :1845  → buildWizardDatePayload (the early follow-up wizard)
 *   sendMoolahPaymentSMS / sendStripePaymentSMS / assertTextSent :1760–1826 → the payment builders
 *   onAICall()                   :1670  → buildAiCallPayload
 *   updateFollowupNote / updateSMSFollowupNote / updateEmailFollowupNote :2455–2663 → the note + template text
 *
 * Time and identity are INJECTED, never read here: `now` = 'YYYY-MM-DDTHH:mm:ss' (today's
 * moment().format('YYYY-MM-DDTHH:mm:ss')), `today` = 'YYYY-MM-DD'; dates are 'YYYY-MM-DD' or null.
 *
 * ── QUIRKS KEPT ON PURPOSE ─────────────────────────────────────────────────────────────────────
 *   • A first-attempt EMAIL sends `payload[statusField] = statusID` (normally 0), not the emailed
 *     status: the check is `toUpperCase() === 'Email'`, which is never true (:2189).
 *   • DateBeforeOverride is 'Invalid date' until the user has pressed Override once (moment(null)),
 *     and keeps the pre-override date after Cancel (:2082).
 *   • DueDate on the created task is the FOLLOW-UP date, 'Invalid date' when there is none — the
 *     `overrideEnabled` / `overrideDate` it tests do not exist (:2114).
 *   • `FollowupMedium` is the lowercase method key ('phone' / 'sms' / 'email').
 *   • A text / email attempt closes with TeamioFollowupStatusID undefined unless a phone status was
 *     picked first (getTeamioFollowupStatusID needs statusID !== 0, :3367) — and then Status is that
 *     PHONE status's label while the payload carries the texted / emailed status (:3220).
 *   • `ClaimNum` is the raw prop on the created tasks (undefined on patient queues → dropped by
 *     JSON) and `claimNum || null` on the updates.
 *   • BelongsToListID: a parent task whose BelongsToListID is undefined still counts as "on a list"
 *     (`undefined !== null`, :2020) and sends undefined.
 *
 * ── FIXED IN THE NEW DRAWER (Nilay 2026-10-02, drawer plan "6a notes") ───────────────────────
 *   bug 3  replaceTemplateVariables fills every placeholder (today skips one after a shorter value).
 *   bug 7  the wizard's date change sends the row's assignment (today: AssignedToEmpID undefined —
 *          `getAssignedToEmployeeID` is not defined in CreateFollowup.vue or mixin.js, :1853).
 *   bug 8  picking an email template never throws (emailNoteForTemplate; today :2618 focuses a
 *          `fnote` ref the email form does not have).
 */

const INVALID_DATE = 'Invalid date';

/** moment(x).format('YYYY-MM-DD') for an injected 'YYYY-MM-DD' / null / undefined (= today). */
function ymdOrInvalid(value, today) {
  if (value === undefined) return today;
  if (value === null || value === '') return INVALID_DATE;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : INVALID_DATE;
}

/** moment(x).format('MM-DD-YYYY'). */
function mdyOrInvalid(value, today) {
  const ymd = ymdOrInvalid(value, today);
  if (ymd === INVALID_DATE) return ymd;
  return `${ymd.slice(5, 7)}-${ymd.slice(8, 10)}-${ymd.slice(0, 4)}`;
}

function upper(s) {
  return typeof s === 'string' ? s.toUpperCase() : '';
}

/** [mainParentTaskData, :3552] */
export function mainParentTaskOf(tasks) {
  return tasks ? tasks.filter(task => task.ParentTaskID === null)[0] : undefined;
}

// ── Status lookups ─────────────────────────────────────────────────────────────────────────────

/**
 * [smsStatusID / emailStatusID, :3135] the status whose label is TEXTED / EMAILED, its id, or null;
 * `false` on queues without the lookup (claims, pre-auths). Statuses not loaded → null (today: throws).
 */
export function messageStatusID(queue, statuses, label) {
  if (!queue || !queue.textedStatusLookup) return false;
  if (!statuses) return null;
  const found = statuses.find(s => upper(s[queue.statusLabelField]) === label);
  return found ? found.FollowupStatusID : null;
}

function statusRow(statuses, id) {
  return statuses ? statuses.filter(s => s.FollowupStatusID === id)[0] : undefined;
}

/** [selectedFollowupStatusName, :3210] — the label the close result reports as Status. */
export function selectedStatusName({ queue, statuses, statusID, medium, smsStatusID, emailStatusID } = {}) {
  if (!statuses || !queue) return undefined;
  let compareTo;
  if (statusID === 0) {
    if (upper(medium) === 'SMS') compareTo = smsStatusID;
    else if (upper(medium) === 'EMAIL') compareTo = emailStatusID;
  } else {
    compareTo = statusID;
  }
  const row = statusRow(statuses, compareTo);
  return row ? row[queue.statusLabelField] : undefined;
}

/** [getTeamioFollowupStatusID, :3365] — undefined unless a (phone) status id was picked. */
export function teamioFollowupStatusIDOf({ statuses, statusID, medium, smsStatusID, emailStatusID } = {}) {
  if (!statuses || statusID === 0) return undefined;
  let row;
  if (upper(medium) === 'PHONE') row = statusRow(statuses, statusID);
  else if (upper(medium) === 'SMS') row = statusRow(statuses, smsStatusID);
  else if (upper(medium) === 'EMAIL') row = statusRow(statuses, emailStatusID);
  else return undefined;
  return row ? row.TeamioFollowupStatusID : undefined;
}

// ── Save ───────────────────────────────────────────────────────────────────────────────────────

export const SAVE_MESSAGES = {
  noStatus: 'Please select a Follow-up Status of the call before saving',
  noDate: 'Please select a Follow-up date before saving',
  noTextedStatus: 'There is no mapping available for "texted" status. Please refer to your Settings or contact Teamio support',
  noSmsTemplates: 'You do not have any template available for SMS related followups. Please <a href=\'/app/wiki\'>click here</a> to create one now, tag it and then come back and try again',
  noTemplateChosen: 'You have not selected a template for this followup',
  noEmailedStatus: 'There is no mapping available for "emailed" status. Please refer to your Settings or contact Teamio support',
  noEmailTemplates: 'You do not have any template available for Email related followups. Please <a href=\'/app/wiki\'>click here</a> to create one now, tag it and then come back and try again',
  emptyNote: 'Follow-up Note is empty. Please try again',
  saveFailed: 'Failed to save follow-up attempt. Please try again.',
  // bug 10, second half (6c): today's save throws here (:2058 parentTask.TaskID) and nothing is said.
  noParentTask: 'This follow-up could not be saved: its earlier attempts have no parent record in Teamio, so there is nothing to add this attempt to. Nothing was saved or sent. Please contact Teamio support.',
};

/**
 * Tasks exist but none is the parent row (ParentTaskID === null) — buildSavePayload would throw
 * (CreateFollowup.vue:2058 reads parentTask.TaskID on an undefined parent). bug 10's second half
 * (drawer plan "6b notes"): the new drawer REFUSES that save with SAVE_MESSAGES.noParentTask.
 */
export function missingParentTask(tasks) {
  return !!(Array.isArray(tasks) && tasks.length > 0 && !tasks.some(task => task && task.ParentTaskID === null));
}

/**
 * save()'s checks in order (:1959–2011): the toast shown INSTEAD of saving, or null.
 * `phoneProblem` = patientPhone.describePhoneProblem(WirelessPhone); `phoneTextable` its isTextablePhone.
 * Last (6c, bug 10): `tasks` with no parent row → noParentTask (today: a throw, no message).
 */
export function saveBlockReason(c = {}) {
  const m = upper(c.medium);
  if (c.statusID === 0 && m !== 'SMS' && m !== 'EMAIL') return SAVE_MESSAGES.noStatus;
  if (c.followupDateVisible && !c.followupDate) return SAVE_MESSAGES.noDate;
  if (m === 'SMS') {
    if (!c.smsStatusID) return SAVE_MESSAGES.noTextedStatus;
    if (!c.smsTemplatesAvailable) return SAVE_MESSAGES.noSmsTemplates;
    if (c.selectedWikiPageID === null) return SAVE_MESSAGES.noTemplateChosen;
    if (!c.phoneTextable) return c.phoneProblem;
  } else if (m === 'EMAIL') {
    if (!c.emailStatusID) return SAVE_MESSAGES.noEmailedStatus;
    if (!c.emailTemplatesAvailable) return SAVE_MESSAGES.noEmailTemplates;
    if (c.selectedWikiPageID === null) return SAVE_MESSAGES.noTemplateChosen;
  }
  if ((c.followupNote || '').trim() === '' && m !== 'EMAIL') return SAVE_MESSAGES.emptyNote;
  if (missingParentTask(c.tasks)) return SAVE_MESSAGES.noParentTask;
  return null;
}

/** Put the template's real links back where the note shows [Link: …] / [Form: …] (:2126–2133). */
export function restoreTemplateLinks(text, linkMap) {
  let out = text;
  if (linkMap && Object.keys(linkMap).length > 0) {
    Object.keys(linkMap).forEach((placeholder) => { out = out.replace(placeholder, linkMap[placeholder]); });
  }
  return out;
}

/**
 * The exact `tasks/addUpdateFollowupTask` payload of save(medium) (:2013–2265).
 *
 * @param {Object} c
 * @param {Object} c.queue        drawerQueues entry
 * @param {string} c.medium       'phone' | 'sms' | 'email' (what the form passes to save)
 * @param {Array}  c.tasks        the queue's tasks result set (null when empty)
 * @param {Array}  [c.patientList] recall / unscheduled: the lists result set (BelongsToListID fallback)
 * @param {number} c.statusID     the phone status picked (0 = none)
 * @param {*}      c.smsStatusID / c.emailStatusID  messageStatusID()'s answers
 * @param {?string} c.followupDate / c.dateBeforeOverride  'YYYY-MM-DD' or null
 * @param {boolean} c.overridden
 * @param {string} c.followupNote, @param {*} c.selectedWikiPageID, @param {Object} c.templateLinkMap
 * @param {*}      c.copyToCommLog drawerQueues.copyToCommLogOf()
 * @param {Object} c.allSettings  for the claim-history copies
 * @param {string} c.siteID, c.patNum, c.teamioPatientID, @param {*} c.userID, c.claimNum
 * @param {Object} c.user         { ODUserNum, Name, DefaultDB }
 * @param {string} c.now, c.today
 */
export function buildSavePayload(c = {}) {
  const q = c.queue;
  const M = upper(c.medium);
  const tasks = c.tasks;
  const parentTask = tasks && tasks.length > 0 ? tasks.filter(task => task.ParentTaskID === null)[0] : null;

  let belongsToListID;
  let belongsToList;
  if (q.detailPageName === 'OverdueRecallDetails' || q.detailPageName === 'unscheduledPatientDetails') {
    belongsToListID = parentTask ? parentTask.BelongsToListID : (c.patientList ? c.patientList[0].ListID : null);
    belongsToList = belongsToListID !== null;
  }

  let firstAttempt = false;
  if (parentTask) firstAttempt = parentTask.Attempts === 0;
  else firstAttempt = tasks ? tasks.length === 0 : true;

  const mainParent = mainParentTaskOf(tasks);
  const totalAttemptsMade = mainParent ? mainParent.Attempts : 0;
  const currentTask = tasks && tasks.length > 0 ? tasks.filter(task => task.Attempts === totalAttemptsMade && task.Active === 1)[0] : null;
  const parentTaskID = firstAttempt ? null : parentTask.TaskID;

  const followupDate = c.followupDate ? ymdOrInvalid(c.followupDate, c.today) : null;
  const dateBeforeOverride = ymdOrInvalid(c.dateBeforeOverride, c.today);
  const overridden = c.overridden ? 1 : 0;
  const attempts = firstAttempt ? 1 : parentTask.Attempts + 1;
  const note = c.followupNote || '';

  let attemptDetails;
  let status;
  if (M === 'SMS') { attemptDetails = 'S'; status = c.smsStatusID; }
  else if (M === 'EMAIL') { attemptDetails = 'E'; status = c.emailStatusID; }
  else { attemptDetails = 'P'; status = c.statusID; }

  const fakeParentTask = {
    CreatedAt: c.now,
    CreatedByEmpID: c.userID,
    AssignedToEmpID: c.userID,
    Attempts: attempts,
    TeamioPatientID: c.teamioPatientID,
    SiteID: c.siteID,
    FollowupDate: followupDate,
    FollowupNote: '',
    DueDate: c.today,
    StatusID: 1,
    TaskTypeID: q.taskTypeID,
    Active: 1,
    ParentTaskID: null,
    BelongsToListID: belongsToList ? belongsToListID : null,
    ClaimNum: c.claimNum,
    Overridden: overridden,
    DateBeforeOverride: dateBeforeOverride,
  };
  fakeParentTask[q.statusField] = status;
  fakeParentTask.AttemptDetails = attemptDetails;
  fakeParentTask[q.statusUpdatedAtField] = c.now;

  const taskToCreate = {
    CreatedAt: c.now,
    CreatedByEmpID: c.userID,
    AssignedToEmpID: c.userID,
    Attempts: attempts,
    FollowupMedium: c.medium,
    TeamioPatientID: c.teamioPatientID,
    SiteID: c.siteID,
    FollowupDate: followupDate,
    FollowupNote: note.trim() || '',
    DueDate: ymdOrInvalid(c.followupDate, c.today),
    StatusID: 1,
    TaskTypeID: q.taskTypeID,
    Active: 1,
    BelongsToListID: belongsToList ? belongsToListID : null,
    ClaimNum: c.claimNum,
    Overridden: overridden,
    DateBeforeOverride: dateBeforeOverride,
  };
  if (M === 'SMS') {
    taskToCreate.SmsTemplateID = c.selectedWikiPageID;
    taskToCreate.SmsText = restoreTemplateLinks(note.trim() || '', c.templateLinkMap);
    taskToCreate[q.statusField] = c.smsStatusID;
  } else if (M === 'EMAIL') {
    taskToCreate.EmailTemplateID = c.selectedWikiPageID;
    taskToCreate[q.statusField] = c.emailStatusID;
  } else {
    taskToCreate[q.statusField] = c.statusID;
  }
  taskToCreate.AttemptDetails = attemptDetails;
  taskToCreate[q.statusUpdatedAtField] = c.now;

  const claimHistory = (taskTypeID, key) => (q.taskTypeID === taskTypeID && c.allSettings && c.allSettings[key] ? c.allSettings[key].PropertyValue : null);
  const oc = claimHistory(2, 'OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY');
  const op = claimHistory(5, 'OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY');
  const user = c.user || {};

  let payload;
  if (!parentTaskID) {
    payload = {
      type: q.typeAllCaps,
      firstAttempt,
      fakeParentTask,
      taskToCreate,
      copyToCommLog: c.copyToCommLog,
      ocCopyToClaimHistory: oc,
      opCopyToClaimHistory: op,
      siteID: c.siteID,
      patNum: c.patNum,
      userODNum: user.ODUserNum,
      employeeName: user.Name || undefined,
      defaultDB: user.DefaultDB,
    };
    // ⚠ 'Email' (mixed case) never equals an upper-cased medium — a first email sends statusID (:2189).
    if (M === 'SMS') payload[q.statusField] = c.smsStatusID;
    else payload[q.statusField] = c.statusID;
  } else {
    const details = `${parentTask.AttemptDetails},${attemptDetails}`;
    const taskToUpdate = {
      taskID: parentTaskID,
      TaskTypeID: q.taskTypeID,
      ClaimNum: c.claimNum ? c.claimNum : null,
      task: {
        UpdatedAt: c.now,
        Attempts: parentTask.Attempts + 1,
        AttemptDetails: details,
        FollowupDate: followupDate,
        StatusID: 3,
        Overridden: overridden,
        DateBeforeOverride: dateBeforeOverride,
      },
    };
    taskToUpdate.task[q.statusField] = status;
    taskToUpdate.task[q.statusUpdatedAtField] = c.now;

    const taskToUpdate2 = {
      taskID: currentTask ? currentTask.TaskID : undefined, // today: throws when no active task matches the attempt count (:2221)
      TeamioPatientID: c.teamioPatientID,
      ClaimNum: c.claimNum ? c.claimNum : null,
      TaskTypeID: q.taskTypeID,
      task: {
        UpdatedAt: c.now,
        Attempts: parentTask.Attempts + 1,
        AttemptDetails: details,
        StatusID: 3,
        Overridden: overridden,
        DateBeforeOverride: dateBeforeOverride,
      },
    };
    taskToUpdate2.task[q.statusField] = status;
    taskToUpdate2.task[q.statusUpdatedAtField] = c.now;

    payload = {
      type: q.typeAllCaps,
      firstAttempt,
      copyToCommLog: c.copyToCommLog,
      ocCopyToClaimHistory: oc,
      opCopyToClaimHistory: op,
      siteID: c.siteID,
      patNum: c.patNum,
      userODNum: user.ODUserNum,
      employeeName: user.Name || undefined,
      defaultDB: user.DefaultDB,
      taskToCreate,
      taskToUpdate,
      taskToUpdate2,
    };
    payload[q.statusField] = status;
  }
  return { payload, firstAttempt, parentTask, attemptDetails, attempts };
}

/**
 * The `closePanel` result after a successful save (:2272–2282). `nextVisit` = nextVisitOf().
 */
export function buildCloseResult(c = {}) {
  const { parentTask, attemptDetails, attempts } = buildSavePayload(c);
  return {
    Status: selectedStatusName(c),
    FollowupDate: c.followupDate ? ymdOrInvalid(c.followupDate, c.today) : null,
    Attempts: attempts,
    AttemptDetails: parentTask && parentTask.AttemptDetails ? `${parentTask.AttemptDetails},${attemptDetails}` : attemptDetails,
    TeamioFollowupStatusID: teamioFollowupStatusIDOf(c),
    StatusUpdatedAtPropertyName: c.queue.statusUpdatedAtField,
    StatusUpdatedAtPropertyNameValue: c.now,
    nextVisit: c.nextVisit === undefined ? null : c.nextVisit,
    Overridden: c.overridden ? 1 : 0,
  };
}

/** Close / Cancel / X with no save (:1948). */
export function closeWithoutSave({ nextVisit } = {}) {
  return { nextVisit: nextVisit === undefined ? null : nextVisit };
}

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

function wallMs(value) {
  if (typeof value !== 'string') return NaN;
  const m = WALL_RE.exec(value);
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
}

/**
 * [patientHasApptScheduledIn60Days, :3417] the close result's `nextVisit`: unscheduled treatment
 * only — the first scheduled appointment 0–60 whole days from `now` (its AptDateTime), else null.
 */
export function nextVisitOf({ detailPageName, scheduledAppointments, now } = {}) {
  if (detailPageName !== 'unscheduledPatientDetails') return null;
  if (!scheduledAppointments || !(scheduledAppointments.length > 0)) return null;
  let ret = null;
  scheduledAppointments.some((appointment) => {
    const raw = (wallMs(appointment.AptDateTime) - wallMs(now)) / 864e5;
    const daysDiff = raw < 0 ? Math.ceil(raw) || 0 : Math.floor(raw);
    if (daysDiff <= 60 && daysDiff >= 0) {
      ret = appointment.AptDateTime;
      return true;
    }
    return false;
  });
  return ret;
}

// ── Note edit, wizard date, payments, AI call ─────────────────────────────────────────────────

/** `tasks/editFollowupNote` (:2756). `copyToCommLog` = drawerQueues.copyToCommLogOf(…, { editNote: true }). */
export function buildEditNotePayload({ queue, siteID, teamioPatientID, userID, claimNum, oldFollowupNote, activity, user, patNum, copyToCommLog } = {}) {
  const u = user || {};
  return {
    SiteID: siteID,
    TeamioPatientID: teamioPatientID,
    EmployeeID: userID,
    TaskTypeID: queue.taskTypeID,
    ClaimNum: claimNum,
    OldFollowupNote: oldFollowupNote,
    FollowupNote: activity.FollowupNote,
    UserODNum: u.ODUserNum,
    PatNum: patNum,
    TaskID: activity.TaskID,
    DefaultDB: u.DefaultDB,
    CopyToCommLog: copyToCommLog,
  };
}

/** The note edit's Save button is enabled only once the text changed (:2794). */
export function editNoteChanged(activity, oldFollowupNote) {
  return activity.FollowupNote !== oldFollowupNote;
}

/**
 * Who a date change is assigned to — what the follow-up TABLE sends for the same row
 * (AdminTable changeDate / followupActions.changeDate): the row's manually assigned employee when
 * it is one of the responsible employees (the picker pre-selects it, setSelectedEmployee), else
 * the RAW responsible setting ("12" or "12|19") and not manually assigned; null when the setting
 * is absent (followupActions' rule). `mainParentTask` is the drawer's parent task row;
 * `responsibleSetting` = drawerQueues.settingValue(allSettings, queue.settings.responsible).
 */
export function wizardAssignment({ mainParentTask, responsibleSetting } = {}) {
  const t = mainParentTask || {};
  const ids = responsibleSetting ? String(responsibleSetting).split('|') : [];
  if (t.EmployeeManuallyAssigned === 1 && t.AssignedToEmpID !== null && t.AssignedToEmpID !== undefined
    && ids.indexOf(String(t.AssignedToEmpID)) !== -1) {
    return { AssignedToEmpID: t.AssignedToEmpID, EmployeeManuallyAssigned: 1 };
  }
  return { AssignedToEmpID: responsibleSetting === undefined ? null : responsibleSetting, EmployeeManuallyAssigned: 0 };
}

/**
 * `tasks/updateFollowupDate` from the early follow-up wizard (:1849).
 * bug 7 FIXED: today sends AssignedToEmpID undefined — `this.getAssignedToEmployeeID` exists
 * nowhere (:1853) — and EmployeeManuallyAssigned 0, which un-assigns a manually assigned row.
 * Here both come from wizardAssignment (the table's rule for the same row).
 */
export function buildWizardDatePayload({ queue, siteID, userID, teamioPatientID, newDate, tasks, claimNum, today, mainParentTask, responsibleSetting } = {}) {
  const a = wizardAssignment({ mainParentTask, responsibleSetting });
  return {
    SiteID: siteID,
    EmployeeID: userID,
    TaskTypeID: queue.taskTypeID,
    AssignedToEmpID: a.AssignedToEmpID,
    TeamioPatientID: teamioPatientID,
    FollowupDate: ymdOrInvalid(newDate, today),
    OldFollowupDate: tasks && tasks.length > 0 ? mdyOrInvalid(tasks[0].FollowupDate, today) : null,
    EmployeeManuallyAssigned: a.EmployeeManuallyAssigned,
    ClaimNum: claimNum || null,
  };
}

function patientNameOf(patient) {
  return patient.PatientName || `${patient.FName} ${patient.LName}`;
}

/** `moolah/createMoolahPaymentRecord` (:1769). */
export function buildMoolahRecordPayload({ siteID, patNum, patientBalance, patient } = {}) {
  return {
    siteID,
    patNum,
    balance: patientBalance,
    patientName: patientNameOf(patient),
    patientPhone: patient.WirelessPhone,
    sendsLink: true,
  };
}

/** `teamio/createPaymentLink` (:1795). */
export function buildStripeLinkPayload({ siteID, patNum, patientBalance, patient } = {}) {
  return {
    siteID,
    patNum,
    balance: patientBalance,
    patientName: patientNameOf(patient),
    patientPhone: patient.WirelessPhone,
  };
}

/** `teamio/sendTextMsg` for a payment link. Moolah: smsText VERBATIM ({{payment_link}} is the backend's). */
export function buildPaymentTextPayload({ siteID, patNum, smsText, user } = {}) {
  const u = user || {};
  return {
    siteID,
    patNum,
    smsText,
    defaultDB: u.DefaultDB,
    userODNum: u.ODUserNum,
    employeeName: u.Name || undefined,
  };
}

/** Stripe only: the link and amounts go in on the frontend (:1807–1811). */
export function stripePaymentText(note, link) {
  let smsText = note;
  smsText = smsText.replace(/\{\{payment_link\}\}/gi, link.paymentUrl);
  smsText = smsText.replace(/\{payment_link\}/gi, link.paymentUrl);
  smsText = smsText.replace(/\{\{total_amount\}\}/gi, `$${link.totalCharge.toFixed(2)}`);
  smsText = smsText.replace(/\{\{balance\}\}/gi, `$${link.originalBalance.toFixed(2)}`);
  return smsText;
}

/**
 * [assertTextSent, :1760] a /send-text-msg 200 body of { success:false } is a REFUSAL, not a send.
 * `optOut` = textConsent.optOutResultFromSendFailure(data). → { sent, reason }.
 */
export function textSendOutcome(data, optOut) {
  if (data && data.success === false) {
    return { sent: false, reason: (optOut && optOut.reason) || data.message || 'The text message was not sent.' };
  }
  return { sent: true, reason: null };
}

/** The success toasts (:1791, :1823). */
export function paymentSentMessage(route, link) {
  if (route === 'stripe') {
    return `Payment link sent! Patient will pay $${link.totalCharge.toFixed(2)} (includes $${link.stripeFee.toFixed(2)} processing fee)`;
  }
  return 'Payment link sent to the patient.';
}

/** triggerAutorecallCall's argument (:1677). */
export function buildAiCallPayload({ queue, siteID, patNum } = {}) {
  return {
    siteID,
    patNum,
    taskID: String(queue.taskTypeID),
    callReason: queue.followupType === 'Recall' ? 'recall' : 'treatment',
  };
}

// ── Note text and SMS template text ────────────────────────────────────────────────────────────

/**
 * [updateFollowupNote's text half, :2473–2518] the phone note. Display strings are injected:
 * `nowDisplay` = moment().format(`${dateFormatStr} hh:mm A`), `nextDateDisplay` =
 * moment(followupDate).format(dateFormatStr) or null when there is no next date, `dosDisplay` =
 * the claim's date of service as MM/DD/YYYY (claims only).
 */
export function composePhoneNote({ existingNote, followupType, isClaims, dosDisplay, attemptNumber, nowDisplay, statusName, nextDateDisplay, isLastAttempt } = {}) {
  let msg = `${followupType}`;
  if (isClaims) msg = `${msg} (DOS: ${dosDisplay})`;
  msg = `${msg} Follow-up #${attemptNumber}: Followed up on ${nowDisplay}. Follow-up status is ${statusName}.`;
  if (nextDateDisplay !== null && nextDateDisplay !== undefined) msg = `${msg} Next follow up date is ${nextDateDisplay}.`;
  if (isLastAttempt) msg = `${msg} Maximum allowed attempts completed.`;

  const current = existingNote || '';
  if (current.trim() === '') return `${msg}\n\n`;
  let note = current;
  const statusMatch = new RegExp('Follow-up status is (.*?)\\.').exec(note);
  const dateMatch = new RegExp('Next follow up date is (.*?)\\.').exec(note);
  const statusWord = statusMatch && statusMatch.length > 0 ? statusMatch[1] : undefined;
  const dateWord = dateMatch && dateMatch.length > 0 ? dateMatch[1] : undefined;
  if (statusWord) note = note.replace(statusWord, statusName);
  if (dateWord) note = note.replace(dateWord, nextDateDisplay === null || nextDateDisplay === undefined ? INVALID_DATE : nextDateDisplay);
  if (!statusWord) note = `${msg}\n\n${note}`;
  return note;
}

/** [updateEmailFollowupNote, :2599] the email attempt's commlog note. */
export function composeEmailNote({ existingNote, followupType, attemptNumber, nowDisplay, statusName, templateName, nextDateDisplay, isLastAttempt } = {}) {
  let msg = `${followupType} Follow-up #${attemptNumber}: Followed up on ${nowDisplay}. Follow-up status is ${statusName}.`;
  msg = `${msg} (template: ${templateName}).`;
  if (nextDateDisplay !== null && nextDateDisplay !== undefined) msg = `${msg} Next follow up date is ${nextDateDisplay}.`;
  if (isLastAttempt) msg = `${msg} Maximum allowed attempts completed.`;
  const current = existingNote || '';
  return current.trim() === '' ? `${msg}\n\n` : `${msg}\n\n${current}`;
}

/**
 * [updateEmailFollowupNote, :2599] the email template picker's change: find the template, compose
 * the note. → { note, templateName } — an unknown / cleared pick leaves the note as it was.
 * bug 8 FIXED: no side effect and nothing that can throw (today ends with
 * `$nextTick(() => this.$refs.fnote.focus())`, and the email form has no `fnote` — a TypeError after
 * every pick, :2618). The form focuses nothing; the status label is whatever the caller passes
 * (undefined on processed pre-auths, whose email save is still refused — bug 5 is KEPT).
 */
export function emailNoteForTemplate({ wikiPages, selectedWikiPageID, existingNote, followupType, attemptNumber, nowDisplay, statusName, nextDateDisplay, isLastAttempt } = {}) {
  const wiki = Array.isArray(wikiPages) ? wikiPages.find(w => w && w.WikiPageID === selectedWikiPageID) : undefined;
  if (!wiki) return { note: existingNote || '', templateName: null };
  return {
    note: composeEmailNote({ existingNote, followupType, attemptNumber, nowDisplay, statusName, templateName: wiki.Name, nextDateDisplay, isLastAttempt }),
    templateName: wiki.Name,
  };
}

/**
 * [replaceTemplateVariables, :2817] {{var}} → a TRUTHY value; anything else stays (so {{payment_link}}
 * reaches the backend / stripePaymentText untouched).
 * bug 3 FIXED: today re-runs a /g regex on the string it just shortened, so lastIndex jumps past the
 * next placeholder — {{patient_balance}} right after {{patient_first_name}} went out literally. One
 * pass over the ORIGINAL text fills every placeholder; a value is never re-scanned.
 */
export function replaceTemplateVariables(str, availableVariables) {
  const vars = availableVariables || {};
  if (typeof str !== 'string') return str;
  return str.replace(/{{(.+?)}}/g, (match, word) => (vars[word] ? String(vars[word]) : match));
}

/** <a> → [Form: …] / [Packet: …] / [Link: …], remembering each original anchor (:2529). */
export function convertLinksToPlaceholders(html, linkMap) {
  return html.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, displayText) => {
    if (!displayText || displayText.trim() === '') return '';
    const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
    let placeholder;
    if (href.indexOf('patient_link_for_form') >= 0) placeholder = `[Form: ${cleanText}]`;
    else if (href.indexOf('patient_link_for_packet') >= 0) placeholder = `[Packet: ${cleanText}]`;
    else placeholder = `[Link: ${cleanText}]`;
    linkMap[placeholder] = match;
    return placeholder;
  });
}

/** [updateSMSFollowupNote's variables, :2562] — `site` = getSite(siteID); `recallDueDisplay` = formatDate(RecallDue). */
export function smsTemplateVariables({ patient, siteID, user, site, patientBalance, isOverdueRecall, recallDueDisplay } = {}) {
  const p = patient || {};
  const s = site || {};
  const vars = {
    patient_first_name: p.FName,
    patient_last_name: p.LName,
    patient_name: p.PatientName,
    patient_wireless_phone: p.WirelessPhone,
    site_id: siteID,
    site_name: user && user.SiteName ? user.SiteName : '',
    site_address1: s.Address1,
    site_address2: s.Address2,
    site_city: s.City,
    site_state: s.State,
    site_zip: s.Zip,
    site_officephone: s.OfficePhone,
    employee_name: user && user.Name ? user.Name : '',
  };
  if (patientBalance) vars.patient_balance = patientBalance.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  if (isOverdueRecall && p.RecallDue) vars.recall_due_date = recallDueDisplay;
  return vars;
}

export const SMS_PREVIEW_URL = 'https://teamio.app/b/xxxxxxx';

/**
 * A wiki template (EditorJS JSON in `content`) → the SMS the form shows (:2523–2595). Paragraph
 * blocks only; links become placeholders; HTML is stripped by the injected `stripHtml` (today: a
 * temp DIV's textContent); trailing whitespace trimmed; variables substituted. {{payment_link}} is
 * never a variable here, so it stays for the backend (Moolah) or stripePaymentText (Stripe).
 * → { text, linkMap, preview } — `preview` = the text with each placeholder as a short URL, or ''.
 */
export function renderSmsTemplate({ content, stripHtml, variables } = {}) {
  const json = JSON.parse(content);
  const linkMap = {};
  let smsText = '';
  json.blocks.filter(b => b.type === 'paragraph').forEach((b) => {
    smsText += stripHtml(convertLinksToPlaceholders(b.data.text, linkMap));
  });
  smsText = smsText.replace(/\s+$/, '');
  const text = replaceTemplateVariables(smsText, variables || {});
  let preview = '';
  if (Object.keys(linkMap).length > 0) {
    preview = text;
    Object.keys(linkMap).forEach((placeholder) => { preview = preview.replace(placeholder, SMS_PREVIEW_URL); });
  }
  return { text, linkMap, preview };
}
